import React, {useEffect, useState} from 'react';
import Modal from '../Modal';
import { useDialoguesContext } from './Provider';
import ButtonRow from '../Button/ButtonRow';
import Button from '../Button/Button';
import Loading from '../Loading';
import Text from '../Formatting/Text';
import { useNavigate } from 'react-router-dom';
import Toggle from '../Button/Toggle';
import API from '../../Services/API';
import { useUnavailableFeatureMessage } from '../../Data/Plans';

const Dialogues = ({children}) => {
    const {dialogues, confirm} = useDialoguesContext();

    useEffect(() => {
        API.setConfirmCallback(confirm);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <>
        {children}
        {dialogues.map((dialogue) => <Dialogue key={dialogue.id} dialogue={dialogue} />)}
    </>
}

const Dialogue = ({dialogue}) => {
    // Must start null, then set to true to trigger the modal to open, then false to close AND REMOVE it
    const [isOpen, setIsOpen] = useState(null);
    const [confirming, setConfirming] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [disabled, setDisabled] = useState(false);

    useEffect(() => {
        setIsOpen(true);
    }, []);

    useEffect(() => {
        if (isOpen === false) {
            setTimeout(dialogue.remove, 200);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const result = async (result) => {
        setDisabled(true);
        if (result) {
            setConfirming(true);
        } else {
            if (dialogue?.options?.onCancel) {
                setCancelling(true);
            }
            try {
                dialogue?.options?.onCancel && await dialogue.options.onCancel();
                !dialogue.waitOnConfirm && setIsOpen(false);
            } catch (e) {
                console.error(e);
                setDisabled(false);
                setConfirming(false);
                setCancelling(false);
            }
        }

        if (dialogue?.promise) {
            await dialogue.promise[0](result);
        }
        if (result && dialogue?.callback) {
            try {
                await dialogue.callback(result);
                setIsOpen(false);
            } catch (e) {
                console.error(e);
                setDisabled(false);
                setConfirming(false);
                setCancelling(false);
            }
        }
        if (dialogue.type === 'info' || dialogue.type === 'featureUnavailable' || dialogue.type === 'roleInsufficient') {
            setIsOpen(false);
        }
        if (dialogue.waitOnConfirm) {
            setIsOpen(false);
        }
    }

    if (dialogue.type === 'confirm') {
        return <ConfirmDialogue {...{isOpen, setIsOpen, result, disabled, confirming, cancelling, ...dialogue}} />

    } else if (dialogue.type === 'info') {
        return <InfoDialogue {...{isOpen, setIsOpen, result, ...dialogue}} />

    } else if (dialogue.type === 'featureUnavailable') {
        return <FeatureUnavailableDialogue {...{isOpen, setIsOpen, result, ...dialogue}} />

    } else if (dialogue.type === 'roleInsufficient') {
        return <RoleInsufficientDialogue {...{isOpen, setIsOpen, result, ...dialogue}} />

    } else if (dialogue.type === 'working') {
        return <WorkingDialogue {...{isOpen, setIsOpen, ...dialogue}} />
    } else {
        console.error('Unknown dialogue type', dialogue.type);
    }
}

const ConfirmDialogue = ({isOpen, setIsOpen, options, result, disabled, confirming, cancelling}) => {
    const [enabled, setEnabled] = useState(!options.confirm);

    return <Modal isOpen={!!isOpen}
            setIsOpen={() => setIsOpen(false)}
            theme={options.theme}
            title={options.title}
            icon={options.icon ?? true}
        >

        <p className="prose-sm text-muted"><Text>{options.message}</Text></p>

        {options.confirm && <Toggle label={options.confirm}
            enabled={enabled}
            onChange={setEnabled}
            className="mt-2"
            ring
        />}

        <ButtonRow>
            <Button theme="secondary"
                onClick={() => result(false)}
                label={options.cancelLabel ?? 'Cancel'}
                disabled={disabled}
                loading={cancelling}
            />
            <Button theme={options.theme ?? 'primary'}
                onClick={() => result(true)}
                label={options.confirmLabel ?? 'Confirm'}
                disabled={disabled || !enabled}
                loading={confirming}
            />
        </ButtonRow>
    </Modal>
}

const InfoDialogue = ({isOpen, setIsOpen, options, result}) => {
    return <Modal isOpen={!!isOpen}
            setIsOpen={() => setIsOpen(false)}
            theme={options.theme ?? 'info'}
            title={options.title}
            dismissible
            icon
        >

        <p className="prose-sm text-muted"><Text>{options.message}</Text></p>

        <ButtonRow>
            <Button theme={options.theme ?? 'primary'}
                onClick={() => result(true)}
                label={options.okLabel ?? 'OK'}
            />
        </ButtonRow>
    </Modal>
}

const FeatureUnavailableDialogue = ({isOpen, setIsOpen, options, result}) => {
    const navigate = useNavigate();

    const message = useUnavailableFeatureMessage(options.feature);

    return <Modal isOpen={!!isOpen} setIsOpen={() => setIsOpen(false)}
            theme={options.theme ?? 'info'}
            title="Feature unavailable"
            dismissible
            icon
        >

        <div className="prose prose-sm text-muted">
            <p>{message}</p>
        </div>

        <ButtonRow>
            <Button theme="secondary"
                onClick={() => result(false)}
                label="Okay"
            />
            <Button theme="primary"
                onClick={() => {
                    result(true)
                    navigate('/billing/plan')
                }}
                label="Upgrade"
            />
        </ButtonRow>
    </Modal>
}

const RoleInsufficientDialogue = ({isOpen, setIsOpen, options, result}) => {

    return <Modal isOpen={!!isOpen}
            setIsOpen={() => setIsOpen(false)}
            theme={options.theme ?? 'info'}
            title="Uh oh, that's not allowed"
            dismissible
            icon
        >

        <div className="prose prose-sm text-muted">
            <p>Sorry, your current role doesn't allow you to access this feature.</p>

            <p>You'll need to speak to a manager to be given access.</p>
        </div>

        <ButtonRow>
            <Button theme="secondary"
                onClick={() => result(false)}
                label="Okay"
            />
        </ButtonRow>
    </Modal>
}

const WorkingDialogue = ({isOpen, setIsOpen, options, callback}) => {
    return <Modal isOpen={!!isOpen} setIsOpen={() => setIsOpen(false)} closer={callback} dismissible={options.dismissible}>

        <Loading size="4x" center />

        <div className="my-6">
            <p className="font-bold text-center">{options.title}</p>

            <p className="mt-3 prose-sm text-muted text-center"><Text>{options.message}</Text></p>

            {options.secondMessage && <p className="mt-3 prose-sm text-xs text-muted text-center text-balance">
                <Text>{options.secondMessage}</Text>
            </p>}
        </div>

    </Modal>
}

export default Dialogues;
